import React from "react";
import type { InvitationsState } from "./useInvitationsState";

interface WizardStepThreeProps {
  state: InvitationsState;
}

const WizardStepThree: React.FC<WizardStepThreeProps> = ({ state }) => {
  const {
    newInvite,
    registrationData,
    isRegistering,
    isIndividual,
    entitySubType,
    formAssessment,
    abnLookupResult,
    RISK_BADGE_CONFIG,
    formatABN,
    formatACN,
    handleCloseForm,
    setWizardStep,
  } = state;

  if (!formAssessment) return null;

  const customerTypeLabel = isIndividual
    ? "Individual"
    : entitySubType === "sole-trader"
      ? "Sole Trader"
      : entitySubType === "company"
        ? "Company"
        : entitySubType === "partnership"
          ? "Partnership"
          : entitySubType === "trust"
            ? "Trust"
            : entitySubType === "association"
              ? "Association"
              : "Government";

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 2,
  };

  return (
    <div>
      <h3
        style={{
          marginBottom: 16,
          fontSize: 16,
          color: "#111",
          fontWeight: 600,
        }}
      >
        Review Registration
      </h3>

      <div
        style={{
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 24px",
          }}
        >
          <div>
            <div style={labelStyle}>Entity</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>
              {newInvite.organisationName || "—"}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Customer Type</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>
              {customerTypeLabel}
            </div>
          </div>
          <div>
            <div style={labelStyle}>First Name</div>
            <div style={{ fontSize: 14, color: "#111" }}>
              {registrationData.firstName || "—"}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Middle Name</div>
            <div style={{ fontSize: 14, color: "#111" }}>
              {registrationData.middleName || "—"}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Last Name</div>
            <div style={{ fontSize: 14, color: "#111" }}>
              {registrationData.lastName || "—"}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Email</div>
            <div style={{ fontSize: 14, color: "#111" }}>
              {registrationData.email || "—"}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Phone</div>
            <div style={{ fontSize: 14, color: "#111" }}>
              {registrationData.phone || "—"}
            </div>
          </div>

          {/* DOB */}
          {(isIndividual || entitySubType === "sole-trader") &&
            registrationData.dateOfBirth &&
            registrationData.dateOfBirth !== "0000-00-00" && (
              <div>
                <div style={labelStyle}>Date of Birth</div>
                <div style={{ fontSize: 14, color: "#111" }}>
                  {(() => {
                    const parts = registrationData.dateOfBirth.split("-");
                    if (
                      parts[0] &&
                      parts[1] &&
                      parts[2] &&
                      parts[0] !== "0000"
                    ) {
                      return new Date(
                        `${parts[0]}-${parts[1]}-${parts[2]}`,
                      ).toLocaleDateString("en-AU", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      });
                    }
                    return "—";
                  })()}
                </div>
              </div>
            )}

          {/* ABN/ACN */}
          {!isIndividual && registrationData.legalEntityCodeData && (
            <div>
              <div style={labelStyle}>
                {registrationData.legalEntityCodeType}
              </div>
              <div style={{ fontSize: 14, color: "#111" }}>
                {registrationData.legalEntityCodeType === "ABN"
                  ? formatABN(registrationData.legalEntityCodeData)
                  : formatACN(registrationData.legalEntityCodeData)}
                {abnLookupResult && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 12,
                      color: abnLookupResult.isActive ? "#16a34a" : "#d97706",
                    }}
                  >
                    ({abnLookupResult.isActive ? "Active" : "Inactive"})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Risk Rating */}
          <div>
            <div style={labelStyle}>Risk Rating</div>
            <div style={{ fontSize: 14, color: "#111" }}>
              <span
                style={{
                  background: RISK_BADGE_CONFIG[formAssessment.riskRating].bg,
                  color: RISK_BADGE_CONFIG[formAssessment.riskRating].color,
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {formAssessment.riskRating === "HIGH"
                  ? "🔴"
                  : formAssessment.riskRating === "MEDIUM"
                    ? "🟡"
                    : "🟢"}{" "}
                {RISK_BADGE_CONFIG[formAssessment.riskRating].label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <button
          type="button"
          className="btn-cancel"
          onClick={() => setWizardStep(2)}
        >
          ← Back
        </button>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="submit"
            className="btn-submit"
            data-testid="generate-link-btn"
            disabled={isRegistering}
            style={{
              opacity: isRegistering ? 0.5 : 1,
              cursor: isRegistering ? "not-allowed" : "pointer",
            }}
          >
            {isRegistering ? "⏳ Registering..." : "Register Customer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardStepThree;
